<template>
  <div>
    <h4>{{ $t(this.graphTitle, { year: this.selectedYear }) }}</h4>
    <div v-if="isDataLoading" class="text-center">
      <p>{{ $t('loading') }}</p>
    </div>
    <div v-else-if="chartData.labels.length > 0">
      <mixed-chart
        :chart-data="chartData"
        :chart-options="chartOptions"
      />
      <h5>{{ $t('ytd-summary') }}</h5>
      <div class="row">
        <div class="col-md-4">

          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>{{ selectedYear }}</th>
                <th>{{ $t('ytd-total') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ $t('permit-applications') }}</td>
                <td>{{ permitApplicationsData.reduce((a, b) => a + b, 0) }}</td>
              </tr>
              <tr>
                <td>{{ $t('permits-issued') }}</td>
                <td>{{ permitsIssuedData.reduce((a, b) => a + b, 0) }}</td>
              </tr>
              <tr>
                <td>{{ $t('delta') }}</td>
                <td>{{ deltaTotal }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else>
      <p>{{ $t('no-data-available') }}</p>
    </div>
  </div>
</template>

<script>
import MixedChart from '@/components/MixedChart';

export default {
  name: 'PermitActivity',
  
  components: {
    MixedChart,
  },

  props: {
    selectedYear: String,
    permitApplications: Array,
    permitsIssued: Array,
    graphTitle: String,
    filterFunction: Function,
    issuesFilterFunction: Function,
  },

  computed: {
    isDataLoading() {
      return !this.permitApplications || !this.permitsIssued || this.permitApplications.length === 0 || this.permitsIssued.length === 0;
    },

    chartData() {
      if (this.isDataLoading) {
        return {
          labels: [],
          datasets: []
        };
      }

      return {
        labels: ['January','February','March','April','May','June','July','August','September','October','November','December'],
        datasets: [
          // Bar chart for permit applications
          {
            label: this.$t('permit-applications'),
            data: this.permitApplicationsData,
            type: 'bar',
            borderColor: 'rgba(79, 129, 189, 1)',
            backgroundColor: 'rgba(79, 129, 189, 0.6)',
            borderWidth: 1
          },
          {
            label: this.$t('permits-issued'),
            data: this.permitsIssuedData,
            type: 'bar',
            borderColor: 'rgba(188, 205, 31, 1)',
            backgroundColor: 'rgba(188, 205, 31, 0.6)',
            borderWidth: 1
          },
          // Line chart for cumulative totals
          {
            label: this.$t('delta'),
            data: this.deltaData,
            type: 'line',
            borderColor: 'rgb(115,115,115)',
            tension: 0.1,
            fill: false
          }
        ]
      };
    },

    permitsIssuedData() {
      return this.calculateMonthlyData(this.permitsIssued, this.issuesFilterFunction);
    },
    permitApplicationsData() {
      return this.calculateMonthlyData(this.permitApplications, this.filterFunction);
    },
    ytdMonth() {
      return new Date(this.permitApplications[this.permitApplications.length - 1].app_start_month).getMonth();
    },
    deltaData() {
      return this.calculateDelta(this.permitApplicationsData, this.permitsIssuedData);
    },
    deltaTotal() {
      return this.deltaData.reduce((a, b) => { 
        if (a) { 
          return a + b; 
        } else { 
          return b; 
        }}, 0);
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: true,
            title: {
              display: true,
              text: this.$t('permit-counts')
            }
          },
          x: {
            title: {
              display: true,
              text: this.$t('month')
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        },
        hover: {
          mode: 'index',
          intersect: false,
        },
        animation: {
          duration: 1000,
        },        
      };
    },
  },

  methods: {
    calculateMonthlyData(yearData, filterFunction) {
      if (!yearData || !Array.isArray(yearData)) {
        return Array(12).fill(0);
      }

      // Filter the data using the provided filter function
      const filteredData = yearData.filter(filterFunction);
      
      // Group and sum totals by month
      const monthlyTotals = {};
      filteredData.forEach(item => {
        const month = new Date(item.app_start_month).getMonth();
        if (!monthlyTotals[month]) {
          monthlyTotals[month] = 0;
        }
        monthlyTotals[month] += parseInt(item.total_count);
      });

      // Return array with 12 months
      const monthlyData = [];
      for (let month = 0; month < 12; month++) {
        monthlyData.push(monthlyTotals[month] || null);
      }

      return monthlyData;
    },

    calculateDelta(permitApplications, permitsIssued) {

      return permitApplications.map((permit_count, index) => {
        if (index <= this.ytdMonth) {
          return permitsIssued[index] - permit_count;
        }
        return null;
      });
    },

  }
}
</script>
